/* eslint unicode-bom: "off" */
// src/components/web/catalogo.js
// ============================================================================
// Catálogo con login/registro/recuperación embebidos en el Drawer del carrito
// Ajustes clave:
//  - Cargamos TODO el catálogo (?all=1) y filtramos/ordenamos/paginamos client-side
//  - Quitamos "Relevancia" y "Novedades"; quedan A–Z, Z–A, Precio asc/desc
//  - "Ver solo con imagen" filtra client-side (nodisponible.png) y arranca OFF
//  - Selector "N / página" sólo limita lo visible en pantalla
//  - Modal de detalle más grande
//  - PasswordInput con ojito SVG (sin FontAwesome)
//  - Validación de sobrestock en carrito (mensaje y bloqueo de continuar)
//  - Fallback de imagen desde /public/nodisponible.png
//  - Recuperar contraseña: código de 6 dígitos con persistencia en sessionStorage
// ============================================================================

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReCAPTCHA from 'react-google-recaptcha';
import { Link } from "react-router-dom";

// Sesión / panel
import { useAuth } from "../../auth/AuthContext";
import UserPanelModal from "../paginas/modals/UserPanelModal.js";
import CuponesModal from "./CuponesModal.js";

/* ============================================================================ */
const CART_KEY = "promequi_cart_v1";
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const HAS_RECAPTCHA = !!RECAPTCHA_SITE_KEY && !isLocalhost;
const api = (path = "") => {
  const p = String(path || "");
  const suf = p.startsWith("/") ? p : `/${p}`;
  return `${API_BASE}${suf}`;
};
const UPLOAD_BASE = API_BASE;
const ALLOWED_HOSTS = new Set([
  "localhost:5001",
  "promequi.com.gt:443",
  "www.promequi.com.gt:443",
]);
const PUBLIC_MARKUP = 0.2;
// Fallback correcto desde /public (CRA/React Vite lo sirven en /)
const FALLBACK_IMG = "/nodisponible.png";
const RESET_EMAIL_KEY = "promequi_reset_email";
const RESET_CODE_KEY = "promequi_reset_code";
const FEEDBACK_TONES = {
  info: "bg-sky-50 border-sky-200 text-sky-800",
  warn: "bg-amber-50 border-amber-200 text-amber-800",
  success: "bg-emerald-50 border-emerald-200 text-emerald-800",
  error: "bg-rose-50 border-rose-200 text-rose-800",
};

// ✔ Formato de moneda con separador de miles por coma
const money = (q) => {
  const n = Number(q || 0);
  const parts = n.toFixed(2).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `Q${parts.join(".")}`;
};

function useDebounce(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function truncateText(str = "", max = 68) {
  const s = String(str || "").trim();
  if (s.length <= max) return s;
  const slice = s.slice(0, max);
  const i = slice.lastIndexOf(" ");
  return (i > 40 ? slice.slice(0, i) : slice) + "…";
}

function b64toBlob(b64Data, contentType = "", sliceSize = 512) {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, { type: contentType });
}

/* ============================================================================ */
/** Password con ojito (SVG, sin FontAwesome) */
function PasswordInput({ value, onChange, placeholder = "", id, required, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 pr-10"
      />
      <button
        type="button"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-800"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        title={show ? "Ocultar" : "Mostrar"}
      >
        {show ? (
          // eye-slash
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.584 10.59a3 3 0 104.243 4.243M9.88 5.09A9.707 9.707 0 0112 5c6 0 9 7 9 7a16.3 16.3 0 01-3.158 3.957M7.11 7.106C4.71 8.62 3 12 3 12s2 4 6 6a10.8 10.8 0 003.3.9" />
          </svg>
        ) : (
          // eye
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
            <circle cx="12" cy="12" r="3.25" strokeWidth="1.8" />
          </svg>
        )}
      </button>
    </div>
  );
}

/* ============================================================================ */
function LogoutButton({ className = "", onDone }) {
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleLogout = async () => {
    try {
      setLoading(true);
      await fetch(api('/api/auth/web/logout'), {
        method: "POST",
        credentials: "include",
      }).catch(() => {});
    } finally {
      logout();
      localStorage.removeItem("auth_user");
      onDone?.();
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleLogout}
      className={`inline-flex items-center font-bold px-4 py-2 rounded-xl bg-rose-600 text-white hover:bg-rose-700 transition ${className}`}
      disabled={loading}
      title="Cerrar sesión"
    >
      {loading ? "Cerrando..." : "CERRAR SESIÓN"}
    </button>
  );
}

/* ============================================================================ */
export default function Catalogo() {
  const { user, isAuthenticated, isSubdistribuidor: isSubCtx, login } = useAuth();

  // Distribuidor/subdistribuidor
  const isDistrib = useMemo(() => {
    if (!isAuthenticated) return false;
    const flagCtx = !!isSubCtx;
    const flagUser =
      !!user?.isSubdistribuidor || !!user?.subDistribuidor || !!user?.subdistribuidor;
    const flagTipo = /sub.?distribuidor|distribuidor/i.test(String(user?.tipoUsuario || ""));
    return flagCtx || flagUser || flagTipo;
  }, [isAuthenticated, isSubCtx, user]);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [cuponesOpen, setCuponesOpen] = useState(false);

  // Filtros (client-side)
  const [query, setQuery] = useState("");
  const dQuery = useDebounce(query, 350);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("name_asc"); // A–Z por defecto
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(true); // arranca OFF
  const [limit, setLimit] = useState(24);
  const [page, setPage] = useState(1);

  // Productos (carga completa y trabajo client-side)
  const [allProducts, setAllProducts] = useState([]); // <- todo el catálogo
  const [loadingProd, setLoadingProd] = useState(false);
  const [errorProd, setErrorProd] = useState("");

  // Categorías
  const [categories, setCategories] = useState([]);

  const feedbackTimeoutRef = useRef(null);
  const [feedback, setFeedback] = useState(null);

  const showFeedback = useCallback((message, tone = "info") => {
    const text = String(message || "").trim();
    if (!text) return;
    setFeedback({ id: Date.now(), message: text, tone });
  }, []);

  useEffect(() => {
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }
    if (!feedback) return;
    // No auto-hide: eliminamos el timeout cosmético para que el feedback permanezca
    // hasta que se reemplace explícitamente por otra acción.
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
        feedbackTimeoutRef.current = null;
      }
    };
  }, [feedback]);

  useEffect(() => () => {
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }
  }, []);

  const normalizeQty = useCallback((value) => {
    const digits = String(value ?? "").replace(/\D/g, "");
    const parsed = digits === "" ? 1 : parseInt(digits, 10);
    return Math.max(1, parsed);
  }, []);

  const getStockValue = useCallback((value) => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return 0;
    return Math.floor(num);
  }, []);

  /* ========================= Helpers precios/imagenes ======================= */
  const priceWithRules = useCallback(
    (base) => {
      const b = Number(base || 0);
      return isDistrib ? b : b * (1 + PUBLIC_MARKUP);
    },
    [isDistrib]
  );

  function buildImgSrc(imagen) {
    let src = String(imagen || "").trim();
    if (!src) return FALLBACK_IMG;
    src = src.replace(/\\/g, "/");
    if (/^https?:\/\//i.test(src)) {
      try {
        const u = new URL(src);
        const hp = `${u.hostname}:${u.port || (u.protocol === "https:" ? "443" : "80")}`;
        if (ALLOWED_HOSTS.has(hp)) {
          return src;
        }
        const baseName = src.split("/").pop();
        return `${UPLOAD_BASE}/uploads/${baseName}`;
      } catch {}
    }
    if (src.startsWith("/uploads/")) return `${UPLOAD_BASE}${src}`;
    if (src.startsWith("/")) return `${UPLOAD_BASE}${src}`;
    return `${UPLOAD_BASE}/uploads/${src}`;
  }

  const isNoImage = useCallback((url) => {
    const u = String(url || "").toLowerCase();
    return u.includes("nodisponible.png");
  }, []);

  /* ============================ Carga de datos ============================== */
  // Categorías
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
  const r = await fetch(api('/api/web/catalogo/categorias'), { credentials: "include" });
        const j = await r.json();
        if (!j?.ok) throw new Error(j?.msg || "Error al cargar categorías");
        if (!cancelled) setCategories(j.data || []);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cargar TODO el catálogo una sola vez
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoadingProd(true);
        setErrorProd("");
        // Traemos TODO: all=1 (sin paginación)
  const url = api('/api/web/catalogo?all=1');
  const r = await fetch(url, { credentials: "include", signal: ac.signal });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j?.ok) throw new Error(j?.msg || "Error al cargar catálogo");

        const rows = (j.data?.rows || []).map((p) => ({
          id: p.id,
          nombre: p.nombre?.trim() || "",
          marca: p.marca?.trim() || "",
          presentacion: p.presentacion?.trim() || "",
          precioBase: Number(p.precio || 0),
          stock: p.stock ?? 0,
          estado: p.estado,
          img: buildImgSrc(p.imagen),
        }));

        setAllProducts(rows);
      } catch (e) {
        if (e.name !== "AbortError") setErrorProd(String(e.message || e));
      } finally {
        setLoadingProd(false);
      }
    })();
    return () => ac.abort();
  }, []);

  // reset de página al cambiar filtros
  useEffect(() => { setPage(1); },
    [dQuery, category, sort, minPrice, maxPrice, onlyAvailable, limit]);

  /* ===================== Filtrado/ordenado/paginado (client) =============== */
  const filteredProducts = useMemo(() => {
    const q = dQuery.trim().toLowerCase();
    const cat = category?.toLowerCase();

    let list = allProducts;

    // Filtro por texto
    if (q) {
      list = list.filter(p => {
        const hay =
          p.nombre.toLowerCase().includes(q) ||
          p.marca.toLowerCase().includes(q) ||
          p.presentacion.toLowerCase().includes(q);
        return hay;
      });
    }

    // Filtro por categoría (marca o slug de marca)
    if (cat && cat !== "all") {
      const asSlug = (s) =>
        String(s)
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s-]/g, "")
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-");

      list = list.filter(p => {
        const m = p.marca || "";
        return m.toLowerCase() === category.toLowerCase() || asSlug(m) === cat;
      });
    }

    // Rango de precios
    const min = String(minPrice).trim() === "" ? null : Number(minPrice);
    const max = String(maxPrice).trim() === "" ? null : Number(maxPrice);
    if (min != null && Number.isFinite(min)) list = list.filter(p => priceWithRules(p.precioBase) >= min);
    if (max != null && Number.isFinite(max)) list = list.filter(p => priceWithRules(p.precioBase) <= max);

    // Solo con imagen
    if (onlyAvailable) list = list.filter(p => !isNoImage(p.img));

    // Orden
    const byName = (a, b) => a.nombre.localeCompare(b.nombre, undefined, { sensitivity: "base" });
    const byPrice = (a, b) => priceWithRules(a.precioBase) - priceWithRules(b.precioBase);

    switch (sort) {
      case "name_desc":
        list = [...list].sort((a, b) => byName(b, a));
        break;
      case "price_asc":
        list = [...list].sort(byPrice);
        break;
      case "price_desc":
        list = [...list].sort((a, b) => byPrice(b, a));
        break;
      case "name_asc":
      default:
        list = [...list].sort(byName);
        break;
    }

    return list;
  }, [allProducts, dQuery, category, minPrice, maxPrice, onlyAvailable, sort, priceWithRules, isNoImage]);

  const total = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));
  const start = (page - 1) * limit;
  const end = start + limit;
  const pageProducts = filteredProducts.slice(start, end);

  /* ============================================================================ */
  // Carrito
  const [cart, setCart] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const backdropRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;
      const revived = {};
      Object.entries(parsed).forEach(([key, value]) => {
        if (!value || typeof value !== "object") return;
        const baseId = value.id ?? Number(key);
        const id = Number.isFinite(Number(baseId)) ? Number(baseId) : null;
        if (id == null) return;
        revived[id] = {
          id,
          nombre: value.nombre || "",
          img: value.img || "",
          stock: getStockValue(value.stock),
          qty: normalizeQty(value.qty),
        };
      });
      setCart(revived);
    } catch {}
  }, [normalizeQty, getStockValue]);
  useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch {}
  }, [cart]);

  useEffect(() => {
    if (!allProducts.length) return;
    const removed = [];
    setCart((prev) => {
      let changed = false;
      const next = {};
      Object.values(prev).forEach((item) => {
        if (!item || typeof item !== "object") return;
        const product = allProducts.find((p) => p.id === item.id);
        if (!product) {
          changed = true;
          removed.push(item.nombre || `#${item.id}`);
          return;
        }
        const stock = getStockValue(product.stock);
        // si el stock llegó a 0, lo quitamos del carrito
        if (stock <= 0) {
          changed = true;
          removed.push(product.nombre || item.nombre || `#${product.id}`);
          return;
        }
        const updated = {
          ...item,
          nombre: product.nombre,
          img: product.img,
          stock,
          // ¡No recortamos qty aquí! Mostramos error si excede stock
          qty: normalizeQty(item.qty),
        };
        if (
          item.nombre !== updated.nombre ||
          item.img !== updated.img ||
          item.stock !== updated.stock ||
          item.qty !== updated.qty
        ) {
          changed = true;
        }
        next[product.id] = updated;
      });
      return changed ? next : prev;
    });
    if (removed.length) {
      showFeedback(`Quitamos del carrito: ${removed.join(", ")}`, "warn");
    }
  }, [allProducts, getStockValue, normalizeQty, showFeedback]);

  useEffect(() => {
    if (drawerOpen) document.documentElement.classList.add("overflow-y-hidden");
    else document.documentElement.classList.remove("overflow-y-hidden");
    return () => document.documentElement.classList.remove("overflow-y-hidden");
  }, [drawerOpen]);

  const cartItems = useMemo(() => Object.values(cart), [cart]);
  const cartCount = useMemo(() => cartItems.reduce((s, it) => s + Number(it.qty || 0), 0), [cartItems]);

  const getEffectivePriceById = useCallback(
    (id) => {
      const p = allProducts.find((x) => x.id === id);
      return p ? priceWithRules(p.precioBase) : 0;
    },
    [allProducts, priceWithRules]
  );

  const cartSubtotal = useMemo(
    () => cartItems.reduce((s, it) => s + getEffectivePriceById(it.id) * Number(it.qty || 0), 0),
    [cartItems, getEffectivePriceById]
  );

  // Cantidades robustas
  const addToCart = (id, qty = 1) => {
    const product = allProducts.find((p) => p.id === id);
    if (!product) return;
    const stock = getStockValue(product.stock);
    if (stock <= 0) {
      showFeedback(`“${product.nombre}” no tiene stock disponible en este momento.`, "warn");
      return;
    }
    const increment = normalizeQty(qty);
    setCart((prev) => {
      const curr = prev[id] || { id: product.id, nombre: product.nombre, img: product.img, stock, qty: 0 };
      const desired = normalizeQty((curr.qty || 0) + increment);
      // si con el "+" llegamos al stock, no dejamos pasar de stock
      const finalQty = Math.min(desired, stock);
      return {
        ...prev,
        [id]: {
          ...curr,
          nombre: product.nombre,
          img: product.img,
          stock,
          qty: finalQty,
        },
      };
    });
  };

  const setQty = (id, qty) => {
    const product = allProducts.find((p) => p.id === id);
    if (!product) return;
    const stock = getStockValue(product.stock);
    if (stock <= 0) {
      // si ya no hay stock, sacamos el ítem
      removeItem(id);
      showFeedback(`“${product.nombre}” ya no tiene stock, lo quitamos del carrito.`, "warn");
      return;
    }
    const requested = normalizeQty(qty);
    // NO recortamos aquí; permitimos sobrestock para mostrar error visual y bloquear continuar
    setCart((prev) => {
      const it = prev[id];
      if (!it) return prev;
      return {
        ...prev,
        [id]: {
          ...it,
          nombre: product.nombre,
          img: product.img,
          stock,
          qty: requested,
        },
      };
    });
  };

  const removeItem = (id) => setCart((prev) => {
    const n = { ...prev };
    delete n[id];
    return n;
  });

  const clearCart = () => setCart({});

  // Validación: hay items con qty > stock
  const overstockItems = useMemo(
    () => cartItems.filter((it) => it.stock > 0 && Number(it.qty || 0) > Number(it.stock || 0)),
    [cartItems]
  );
  const hasOverstock = overstockItems.length > 0;

  /* ============================================================================ */
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [step, setStep] = useState(0);

  const [usuarioId, setUsuarioId] = useState(null);
  const [guestNombre, setGuestNombre] = useState("");
  const [guestDpi, setGuestDpi] = useState("");
  const [guestWhatsapp, setGuestWhatsapp] = useState("");
  const [coupon, setCoupon] = useState("");
  const [cupones, setCupones] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [direccion, setDireccion] = useState("");
  const [volLevels, setVolLevels] = useState([]);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [boleta, setBoleta] = useState("");
  const [banco, setBanco] = useState("");
  const [nit, setNit] = useState("");
  const [uploadPct, setUploadPct] = useState(null);
  const [acceptedReturnsTC, setAcceptedReturnsTC] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      setUsuarioId(user.id);
    } else {
      setUsuarioId(null);
      setCoupon("");
      setSelectedCoupon(null);
      setCupones([]);
    }
  }, [isAuthenticated, user]);

  const normalizeCoupons = useCallback((raw) => {
    if (!raw) return [];
    const src = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.cupones)
      ? raw.cupones
      : [];
    return src
      .map((c, i) => ([
        c.id ?? c.codigo ?? `c${i}`,
        c.codigo ?? c.code ?? (typeof c === "string" ? c : ""),
        c.descripcion ?? c.description ?? "",
        Number(c.porcentaje_descuento ?? c.porcentaje ?? 0),
        c.producto_id ?? c.productoId ?? null,
        c.producto_nombre_snapshot ?? c.producto_nombre ?? "",
        c.color_hex,
        c.fecha_fin,
        c.imagen ?? c.image ?? null
      ]))
      .filter(([, codigo]) => !!codigo)
      .map(([id,codigo,descripcion,porcentaje_descuento,producto_id,producto_nombre,color_hex,fecha_fin,imagen]) =>
        ({ id,codigo,descripcion,porcentaje_descuento,producto_id,producto_nombre,color_hex,fecha_fin, imagen })
      );
  }, []);

  const fetchUserCoupons = useCallback(async () => {
    if (!isAuthenticated || !isDistrib) return;
    try {
  const r = await fetch(api('/api/web/cupones'), { credentials: "include" });
      const j = await r.json().catch(() => ({}));
      setCupones(normalizeCoupons(j));
    } catch {}
  }, [isAuthenticated, isDistrib, normalizeCoupons]);

  useEffect(() => {
    if (isAuthenticated && isDistrib) fetchUserCoupons();
  }, [isAuthenticated, isDistrib, fetchUserCoupons]);

  // Cargar niveles de descuento por volumen (público)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(api('/api/configuraciones/volume-discounts'));
        const j = await r.json().catch(() => ([]));
        if (Array.isArray(j)) setVolLevels(j);
      } catch {}
    })();
  }, []);

  const hasActiveCouponFor = useCallback((productId) => {
    if (!isAuthenticated || !isDistrib) return false;
    if (!Array.isArray(cupones) || cupones.length === 0) return false;
    const now = Date.now();
    return cupones.some(c => {
      const pid = Number(c.producto_id ?? c.productoId);
      if (!pid || pid !== Number(productId)) return false;
      if (!c.fecha_fin) return true;
      const t = new Date(c.fecha_fin).getTime();
      return Number.isFinite(t) ? t >= now : true;
    });
  }, [isAuthenticated, isDistrib, cupones]);

  const openCheckout = () => {
    if (isAuthenticated && user?.id) {
      setUsuarioId(user.id);
      if (user?.direccion && !direccion) setDireccion(user.direccion);
      setStep(1);
    } else {
      setStep(0);
    }
    setCheckoutOpen(true);
  };

  const requiereDireccion = cartSubtotal >= 1000;
  const envio = useMemo(() => {
    if (!requiereDireccion) return 0;
    if (!direccion || !direccion.trim()) return 0;
    const d = direccion.toLowerCase();
    const esCapital =
      d.includes("ciudad de guatemala") ||
      (d.includes("guatemala") && d.includes("zona"));
    return esCapital ? 0 : 40;
  }, [requiereDireccion, direccion]);

  const couponDiscount = useMemo(() => {
    if (!isDistrib) return 0;
    if (!selectedCoupon || !selectedCoupon.producto_id || !selectedCoupon.porcentaje_descuento) return 0;
    const prodId = Number(selectedCoupon.producto_id);
    const item = cartItems.find((it) => Number(it.id) === prodId);
    if (!item) return 0;
    const unit = getEffectivePriceById(item.id);
    const pct = Number(selectedCoupon.porcentaje_descuento) / 100;
    return unit * item.qty * pct;
  }, [selectedCoupon, cartItems, getEffectivePriceById, isDistrib]);

  const subtotalConDescuento = useMemo(
    () => Math.max(0, cartSubtotal - couponDiscount),
    [cartSubtotal, couponDiscount]
  );

  const totalUnits = useMemo(
    () => cartItems.reduce((s, it) => s + Math.max(1, parseInt(it.qty || 1, 10)), 0),
    [cartItems]
  );

  const volumenPct = useMemo(() => {
    if (!Array.isArray(volLevels) || volLevels.length === 0) return 0;
    let pct = 0;
    for (const lvl of volLevels) {
      if (!lvl || Number(lvl.active) !== 1) continue;
      const tu = lvl.threshold_units != null ? Number(lvl.threshold_units) : null;
      const tv = lvl.threshold_value != null ? Number(lvl.threshold_value) : null;
      const meetsUnits = tu != null && totalUnits >= tu;
      const meetsValue = tv != null && subtotalConDescuento >= tv;
      if (meetsUnits || meetsValue) pct = Math.max(pct, Number(lvl.discount_percent || 0));
    }
    return pct;
  }, [volLevels, totalUnits, subtotalConDescuento]);

  const volumeDiscount = useMemo(
    () => Math.max(0, subtotalConDescuento * (Number(volumenPct || 0) / 100)),
    [subtotalConDescuento, volumenPct]
  );

  const totalFinal = useMemo(
    () => Math.max(0, subtotalConDescuento - volumeDiscount) + envio,
    [subtotalConDescuento, volumeDiscount, envio]
  );
  // Regla fiscal: solo solicitar NIT cuando la compra (tras descuentos) es >= Q100
  const requiereNit = (subtotalConDescuento - volumeDiscount) >= 100;

  const validarPaso0 = () => {
    if (usuarioId) return true;
    const dpiOK = /^\d{13}$/.test(guestDpi);
    const waDigits = guestWhatsapp.replace(/\D/g, "");
    const waOK = waDigits.length === 8;
    return guestNombre.trim().length > 2 && dpiOK && waOK;
  };

  const enviarPedido = async () => {
    try {

      setSending(true);
      setSendError("");
      setUploadPct(0);

      if (hasOverstock) {
        setSendError("Corrige las cantidades que superan el stock disponible antes de enviar.");
        setSending(false);
        return;
      }

      const items = cartItems.map((it) => ({
        id: it.id,
        nombre: it.nombre,
        precio: getEffectivePriceById(it.id),
        qty: Math.max(1, parseInt(it.qty || 1, 10)),
        imagen: it.img,
      }));

      const fd = new FormData();
      if (usuarioId) fd.append("usuarioId", String(usuarioId));
      fd.append("nombre", usuarioId ? "" : guestNombre);
      fd.append("dpi", usuarioId ? "" : guestDpi);
      fd.append("whatsapp", usuarioId ? "" : guestWhatsapp);
      if (requiereDireccion) fd.append("direccionEntrega", direccion || "");
      fd.append("pedido", JSON.stringify(items));
  // Enviar totales detallados (backend recalcula envío y total)
  fd.append("subtotal", String(cartSubtotal));
  fd.append("descuentoCupon", String(isDistrib ? couponDiscount : 0));
  fd.append("descuentoVolumen", String(volumeDiscount));
      fd.append("boleta", boleta || "");
      fd.append("banco", banco || "");
  // Si no requiere NIT (< Q100 tras descuentos), no enviamos NIT
  fd.append("nit", requiereNit ? (nit || "") : "");
      fd.append("cupon", isDistrib ? (coupon || "") : "");
      if (voucher) fd.append("imagen", voucher);

      const xhr = new XMLHttpRequest();
  xhr.open("POST", api('/api/pedidosweb'), true);
      xhr.responseType = "json";

      xhr.upload.onprogress = (e) => {
        if (e.length ) {
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploadPct(pct);
        } else {
          setUploadPct((p) => (p == null ? 0 : p));
        }
      };

      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response ?? JSON.parse(xhr.responseText || "{}"));
          } else {
            reject(new Error(`Error ${xhr.status}: no se pudo enviar el pedido`));
          }
        };
        xhr.onerror = () => reject(new Error("Error de red al enviar el pedido"));
        xhr.send(fd);
      });

      const data = result || {};
      if (!data.ok) throw new Error(data.message || "Error al guardar pedido");

      setUploadPct(100);

      if (data.pdfBase64) {
        const blob = b64toBlob(data.pdfBase64, "application/pdf");
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      }

  // === Enviar resumen por WhatsApp ===
try {
  const phone = "50259786302"; // con código de país, sin "+"
  const cliente = usuarioId
    ? `Cliente registrado:
• ID: ${usuarioId}
• Email: ${user?.email ?? "—"}
• Tel: ${user?.telefono ?? "—"}`
    : `Cliente invitado:
• Nombre: ${guestNombre || "—"}
• DPI: ${guestDpi || "—"}
• WhatsApp: +502 ${guestWhatsapp || "—"}`;

  const productosTxt = cartItems.map((it) => {
    const unit = getEffectivePriceById(it.id);
    const qty = Math.max(1, parseInt(it.qty || 1, 10));
    const lineTotal = unit * qty;
    return `• ${it.nombre} x${qty} — ${money(lineTotal)}`;
  }).join("\n");

  const envioTxt = requiereDireccion ? `\nDirección: ${direccion || "—"}` : "";
  const bancoTxt = banco ? `\nBanco: ${banco} (boleta ${boleta || "—"})` : "";
  const nitTxt = requiereNit && nit ? `\nNIT: ${nit}` : "";
  const cuponTxt = (isDistrib && selectedCoupon?.codigo)
    ? `\nCupón: ${selectedCoupon.codigo} (${selectedCoupon.porcentaje_descuento}% en ${selectedCoupon.producto_nombre || "producto"})`
    : "";

  const mensaje =
`🧾 *Nuevo pedido web*

${cliente}

🛒 Carrito:
${productosTxt}

Subtotal: ${money(cartSubtotal)}
${isDistrib && couponDiscount > 0 ? `Descuento cupón: -${money(couponDiscount)}\n` : ""}${volumeDiscount > 0 ? `Descuento por volumen: -${money(volumeDiscount)}\n` : ""}Envío: ${money(envio)}
*Total:* ${money(totalFinal)}
${envioTxt}${nitTxt}${bancoTxt}${cuponTxt}`;

  // Enviar automáticamente al WhatsApp admin vía backend (Flaxxa API)
  try {
    const subtotalRaw = cartSubtotal;
    const descuentoCupon = isDistrib ? Math.max(0, couponDiscount) : 0;
    const descuentoVolumen = Math.max(0, volumeDiscount);
    const subtotal = Math.max(0, subtotalRaw - descuentoCupon - descuentoVolumen);
    const payload = {
      id: data?.id || null,
      usuarioId: usuarioId || null,
      nombre: usuarioId ? (user?.nombre || user?.nombreCompleto || `${user?.nombres || ''} ${user?.apellidos || ''}`.trim()) : (guestNombre || ''),
      empresa: usuarioId ? (user?.empresa || null) : null,
      direccion: direccion || '',
      whatsapp: usuarioId ? (user?.telefono || user?.whatsapp || '') : (guestWhatsapp || ''),
      dpi: usuarioId ? (user?.dpi || '') : (guestDpi || ''),
      nit: requiereNit ? (nit || '') : '',
      boleta: boleta || '',
      banco: banco || '',
      cupon: (isDistrib && selectedCoupon?.codigo) ? selectedCoupon.codigo : '',
      items: cartItems.map(it => ({ nombre: it.nombre, qty: Math.max(1, parseInt(it.qty || 1, 10)), precio: getEffectivePriceById(it.id) })),
      subtotalRaw,
      descuentoCupon,
      descuentoVolumen,
      subtotal,
      envio,
      totalFinal,
    };
    fetch('/api/whatsapp/admin-summary', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    }).catch(() => {});
  } catch {}
} catch {}


      clearCart();
      // Reset de estados de checkout
      setCheckoutOpen(false);
      setStep(0);
      setCoupon("");
      setSelectedCoupon(null);
      setUsuarioId(null);
      setGuestNombre("");
      setGuestDpi("");
      setGuestWhatsapp("");
      setDireccion("");
      setBoleta("");
      setBanco("");
      setNit("");
      setVoucher(null);
      setUploadPct(null);
      setAcceptedReturnsTC(false);
      // Limpiar persistencia de recuperación, si existiera
      try {
        sessionStorage.removeItem(RESET_EMAIL_KEY);
        sessionStorage.removeItem(RESET_CODE_KEY);
      } catch {}

      // Mensaje de éxito y recarga inmediata para refrescar catálogo y stock en tiempo real
      showFeedback('Pedido Enviado Correctamente', 'success');
      try { window.location.reload(); } catch {}
    } catch (e) {
      setSendError(String(e.message || e));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const a = document.activeElement;
      const isTyping =
        a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.isContentEditable);
      if (isTyping) return;
      setDrawerOpen(false);
      setCheckoutOpen(false);
      setDetailOpen(false);
      setUploadPct(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [authErr, setAuthErr] = useState("");
  const [loadingAuth, setLoadingAuth] = useState(false);

  const [loginData, setLoginData] = useState({ user: "", pass: "" });
  const [signupData, setSignupData] = useState({
    nombre: "",
    empresa: "",
    direccion: "",
    telefono: "",
    correo: "",
    nit: "",
    pass: "",
    passConfirm: "",
  });
  const [forgotEmail, setForgotEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    return sessionStorage.getItem(RESET_EMAIL_KEY) || "";
  });
  const [verifyCode, setVerifyCode] = useState(() => {
    if (typeof window === "undefined") return "";
    return sessionStorage.getItem(RESET_CODE_KEY) || "";
  });
  const [resetData, setResetData] = useState({ pass: "", confirm: "" });
  const recaptchaRef = useRef();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const value = String(forgotEmail || "").trim();
    if (value) sessionStorage.setItem(RESET_EMAIL_KEY, value);
    else sessionStorage.removeItem(RESET_EMAIL_KEY);
  }, [forgotEmail]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const value = String(verifyCode || "").trim();
    if (value) sessionStorage.setItem(RESET_CODE_KEY, value);
    else sessionStorage.removeItem(RESET_CODE_KEY);
  }, [verifyCode]);

  useEffect(() => {
    if (authMode === "login") {
      setVerifyCode("");
      setResetData({ pass: "", confirm: "" });
    }
    if (authMode === "forgot") {
      setVerifyCode("");
    }
  }, [authMode]);

  const startLoginFromCheckout = () => {
    setCheckoutOpen(false);
    setDrawerOpen(true);
    setAuthOpen(true);
    setAuthMode("login");
    setAuthErr("");
  };

  const doLogin = async (e) => {
    e?.preventDefault?.();
    setAuthErr("");
    try {
      setLoadingAuth(true);
  let recaptchaToken = '';
  try { if (HAS_RECAPTCHA && recaptchaRef.current) { recaptchaToken = await recaptchaRef.current.executeAsync(); recaptchaRef.current.reset(); } } catch {}
      const r = await fetch(api('/api/auth/web/loginWeb'), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: loginData.user, pass: loginData.pass, recaptchaToken }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) {
        setAuthErr(j?.message || j?.msg || "Credenciales inválidas");
        return;
      }
      login(j.user);
      setAuthOpen(false);
      setUsuarioId(j.user?.id || null);
      fetchUserCoupons();
    } catch (err) {
      setAuthErr(String(err.message || err));
    } finally {
      setLoadingAuth(false);
    }
  };

  const doSignup = async (e) => {
    e?.preventDefault?.();
    setAuthErr("");

    if (signupData.pass !== signupData.passConfirm) {
      setAuthErr("Las contraseñas no coinciden.");
      return;
    }

    try {
      setLoadingAuth(true);
  let recaptchaToken = '';
  try { if (HAS_RECAPTCHA && recaptchaRef.current) { recaptchaToken = await recaptchaRef.current.executeAsync(); recaptchaRef.current.reset(); } } catch {}
      const r = await fetch(api('/api/auth/web/signup'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          nombre: signupData.nombre,
          empresa: signupData.empresa,
          direccion: signupData.direccion,
          telefono: signupData.telefono,
          correo: signupData.correo,
          nit: signupData.nit,
          pass: signupData.pass,
          passConfirm: signupData.passConfirm,
          recaptchaToken,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!j?.ok) throw new Error(j?.msg || "No se pudo crear la cuenta");
      setAuthMode("login");
      setAuthErr("✓ Tu solicitud fue enviada. Te avisaremos por correo.");
    } catch (err) {
      setAuthErr(String(err.message || err));
    } finally {
      setLoadingAuth(false);
    }
  };

  const onForgotSend = async () => {
    const email = String(forgotEmail || "").trim().toLowerCase();
    if (!email) {
      setAuthErr("Ingresa tu correo electrónico.");
      return;
    }
    setForgotEmail(email);
    try {
      setLoadingAuth(true);
      setAuthErr("");
  let recaptchaToken = '';
  try { if (HAS_RECAPTCHA && recaptchaRef.current) { recaptchaToken = await recaptchaRef.current.executeAsync(); recaptchaRef.current.reset(); } } catch {}
      const r = await fetch(api('/api/auth/web/password/forgot'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, recaptchaToken }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.msg || "No se pudo enviar el código");
      setVerifyCode("");
      setAuthMode("verify");
    } catch (e) {
      setAuthErr(String(e.message || e));
    } finally {
      setLoadingAuth(false);
    }
  };
  const onVerifyCode = async () => {
    const email = String(forgotEmail || "").trim().toLowerCase();
    const code = String(verifyCode || "").replace(/\D/g, "").slice(0, 6);
    if (!email) {
      setAuthErr("Necesitamos el correo para validar el código.");
      setAuthMode("forgot");
      return;
    }
    if (code.length < 6) {
      setAuthErr("Ingresa el código de 6 dígitos que recibiste por correo.");
      return;
    }
    setVerifyCode(code);
    try {
      setLoadingAuth(true);
      setAuthErr("");
  let recaptchaToken = '';
  try { if (HAS_RECAPTCHA && recaptchaRef.current) { recaptchaToken = await recaptchaRef.current.executeAsync(); recaptchaRef.current.reset(); } } catch {}
      const r = await fetch(api('/api/auth/web/password/verify'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, code, recaptchaToken }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.msg || "Código inválido o vencido");
      setAuthMode("reset");
    } catch (e) {
      setAuthErr(String(e.message || e));
    } finally {
      setLoadingAuth(false);
    }
  };
  const onResetPass = async () => {
    if (!resetData.pass || resetData.pass !== resetData.confirm) {
      setAuthErr("Las contraseñas no coinciden");
      return;
    }
    const email = String(forgotEmail || "").trim().toLowerCase();
    const code = String(verifyCode || "").replace(/\D/g, "").slice(0, 6);
    if (!email || !code) {
      setAuthErr("Debemos validar nuevamente tu correo y código.");
      setAuthMode("forgot");
      return;
    }
    try {
      setLoadingAuth(true);
      setAuthErr("");
  let recaptchaToken = '';
  try { if (HAS_RECAPTCHA && recaptchaRef.current) { recaptchaToken = await recaptchaRef.current.executeAsync(); recaptchaRef.current.reset(); } } catch {}
      const r = await fetch(api('/api/auth/web/password/reset'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          code,
          pass: resetData.pass,
          passConfirm: resetData.confirm,
          recaptchaToken,
        }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.msg || "No se pudo actualizar la contraseña");
      setAuthMode("login");
      setAuthErr("Tu contraseña fue actualizada. Ahora puedes iniciar sesión.");
    } catch (e) {
      setAuthErr(String(e.message || e));
    } finally {
      setLoadingAuth(false);
    }
  };

  const userType = !isAuthenticated ? "guest" : isDistrib ? "distribuidor" : "particular";

  const cardClass = useMemo(() => {
    const base = "flex flex-col items-start px-3 py-2 rounded-xl border transition-colors";
    if (userType === "distribuidor") return `${base} bg-amber-400 border-amber-500 text-slate-900`;
    if (userType === "particular") return `${base} bg-[var(--brand)] border-[var(--brand)] text-white`;
    return `${base} bg-white border-slate-300 text-slate-800`;
  }, [userType]);

  const subTextClass = useMemo(() => {
    if (userType === "distribuidor") return "text-[11px] text-slate-900 -mt-0.5";
    if (userType === "particular") return "text-[11px] text-white -mt-0.5";
    return "text-[11px] text-slate-600 -mt-0.5";
  }, [userType]);

  const detailStock = detailItem ? Math.max(0, Number(detailItem.stock ?? 0)) : 0;
  const detailHasStock = detailStock > 0;

  return (
    <div className="bg-white text-slate-800">
      <style>{`
        :root{ --brand:#244A9B; --brand-soft:#9EC2FF; --ink:#0F1D43; }
        .soft{box-shadow:0 12px 30px rgba(15,29,67,.06),0 6px 14px rgba(15,29,67,.05)}
        .btn{display:inline-flex;align-items:center;justify-content:center;border-radius:0.75rem;padding:.75rem 1.25rem;font-weight:600;transition:.15s}
        .btn:active{transform:scale(.99)}
        .btn-brand{background:var(--brand);color:#fff}
        .btn-brand:hover{filter:brightness(.93)}
        .btn-light{background:#fff;color:#334155;border:1px solid #e2e8f0}
        .nav-link{font-size:.95rem;font-weight:700;letter-spacing:.02em;color:#475569}
        .nav-link:hover{color:#0f172a}
      `}</style>

      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-24 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" aria-label="Inicio Promequi">
              <img src="/assets/logo.png" alt="Logo Promequi" className="h-24 w-auto" />
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link className="nav-link" to="/catalogo">TIENDA EN LÍNEA</Link>

              {isAuthenticated && isDistrib && (
                <button
                  className="nav-link cursor-pointer"
                  onClick={() => {
                    setCuponesOpen(true);
                    fetchUserCoupons();
                  }}
                >
                  CUPONES
                </button>
              )}

              <div className="flex items-center gap-3">
                <button
                  className="relative inline-flex items-center justify-center gap-2 pl-3 pr-3 py-2 rounded-full border border-slate-200 bg-slate-800 hover:bg-slate-600"
                  aria-label="Abrir carrito"
                  title="Carrito"
                  onClick={() => setDrawerOpen(true)}
                >
                  <p className="px-1 font-bold text-white">VER MI CARRITO</p>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 10-8 0v4M5 9h14l-1.5 9a2 2 0 01-2 1.7H8.5a2 2 0 01-2-1.7L5 9z" />
                  </svg>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] rounded-full bg-red-500 text-white text-[10px] leading-5 px-1 text-center">
                      {cartCount}
                    </span>
                  )}
                </button>

                <div className={cardClass}>
                  <div className="text-sm font-semibold">
                    {isAuthenticated ? user?.email || "—" : "Usuario invitado"}
                  </div>
                  <div className={subTextClass}>
                    {isAuthenticated ? (isDistrib ? "Distribuidor" : "Particular") : "Invitado"}
                  </div>
                </div>

                {isAuthenticated && (
                  <>
                    <button
                      type="button"
                      onClick={() => setPanelOpen(true)}
                      className="inline-flex items-center font-bold px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition"
                    >
                      VER MI PANEL
                    </button>
                    <LogoutButton />
                  </>
                )}
              </div>
            </nav>

            {/* Mobile */}
            <div className="flex items-center gap-3">
              <button
                className="relative inline-flex items-center justify-center p-2 rounded-full border border-slate-200 hover:bg-slate-50 md:hidden"
                aria-label="Abrir carrito"
                title="Carrito"
                onClick={() => setDrawerOpen(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 10-8 0v4M5 9h14l-1.5 9a2 2 0 01-2 1.7H8.5a2 2 0 01-2-1.7L5 9z" />
                </svg>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] rounded-full bg-red-500 text-white text-[10px] leading-5 px-1 text-center">
                    {cartCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setMobileOpen((v) => !v)}
                className="md:hidden p-2 rounded-lg border border-slate-200"
                aria-label="Abrir menú"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col gap-3 text-sm">
                <Link to="/catalogo" className="py-2" onClick={() => setMobileOpen(false)}>
                  TIENDA EN LÍNEA
                </Link>

                {isAuthenticated && isDistrib && (
                  <button
                    className="py-2 text-left"
                    onClick={() => {
                      setCuponesOpen(true);
                      setMobileOpen(false);
                      fetchUserCoupons();
                    }}
                  >
                    CUPONES
                  </button>
                )}

                <div className={cardClass}>
                  <div className="text-sm font-semibold">
                    {isAuthenticated ? user?.email || "—" : "Usuario invitado"}
                  </div>
                  <div className={subTextClass}>
                    {isAuthenticated ? (isDistrib ? "Distribuidor" : "Particular") : "Invitado"}
                  </div>
                </div>

                {isAuthenticated && (
                  <>
                    <button
                      className="py-2 text-left font-bold px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition"
                      onClick={() => {
                        setPanelOpen(true);
                        setMobileOpen(false);
                      }}
                    >
                      VER MI PANEL
                    </button>
                    <LogoutButton className="w-full justify-center" onDone={() => setMobileOpen(false)} />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {feedback && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div
            className={`rounded-xl border px-4 py-3 text-sm shadow-sm ${FEEDBACK_TONES[feedback.tone] || FEEDBACK_TONES.info}`}
            role="status"
          >
            {feedback.message}
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: "var(--ink)" }}>
            TIENDA EN LÍNEA
          </h1>
          <p className="text-slate-600 mt-2">Explora nuestros productos y añade al carrito.</p>
        </div>
      </section>

      {/* Filtros */}
      <section className="pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
            <div className="lg:col-span-4">
              <label className="sr-only">Buscar</label>
              <input
                type="search"
                placeholder="Buscar producto, marca o presentación…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-300"
              />
            </div>

            <div className="lg:col-span-3">
              <label className="sr-only">Categoría</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 bg-white"
              >
                <option value="all">Todas las categorías</option>
                {categories.map((c, i) => (
                  <option key={`${c.slug || c.nombre}-${i}`} value={c.slug || c.nombre}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-3">
              <label className="sr-only">Ordenar</label>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 bg-white"
              >
                <option value="name_asc">Nombre A–Z</option>
                <option value="name_desc">Nombre Z–A</option>
                <option value="price_asc">Precio: menor a mayor</option>
                <option value="price_desc">Precio: mayor a menor</option>
              </select>
            </div>

            <div className="lg:col-span-2 flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="w-5 h-5"
                  checked={onlyAvailable}
                  onChange={(e) => setOnlyAvailable(e.target.checked)}
                />
                Ver solo con imagen
              </label>
            </div>

            <div className="lg:col-span-12 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">Q</span>
                <input
                  inputMode="decimal"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  placeholder="Precio mín."
                  className="w-full rounded-xl border pl-7 pr-3 py-2"
                />
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">Q</span>
                <input
                  inputMode="decimal"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="Precio máx."
                  className="w-full rounded-xl border pl-7 pr-3 py-2"
                />
              </div>
              <div>
                <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="w-full rounded-xl border px-3 py-2 bg-white">
                  {[12, 24, 36, 48, 72].map((n) => (
                    <option key={n} value={n}>{n} / página</option>
                  ))}
                </select>
              </div>
              <div className="text-right text-sm text-slate-500 self-center">
                {loadingProd
                  ? "Cargando..."
                  : errorProd
                  ? "—"
                  : `${total} resultado${total === 1 ? "" : "s"}`}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <main className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {errorProd && <div className="text-red-600 mb-4">{errorProd}</div>}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4" id="catalogGrid">
            {(loadingProd ? Array.from({ length: Math.min(limit, 12) }) : pageProducts).map((item, idx) => {
              if (loadingProd) {
                return (
                  <div key={`sk-${idx}`} className="bg-white rounded-2xl p-4 border border-slate-200 soft">
                    <div className="aspect-square rounded-xl bg-slate-100 animate-pulse" />
                    <div className="mt-3 h-4 bg-slate-100 rounded animate-pulse" />
                    <div className="mt-2 h-3 bg-slate-100 rounded animate-pulse w-2/3" />
                    <div className="mt-2 h-3 bg-slate-100 rounded animate-pulse w-1/2" />
                    <div className="mt-3 h-4 bg-slate-100 rounded animate-pulse w-1/3" />
                    <div className="mt-3 h-8 bg-slate-100 rounded animate-pulse" />
                  </div>
                );
              }

              const p = item;
              const available = Math.max(0, Number(p.stock ?? 0));
              const hasStock = available > 0;

              return (
                <article
                  key={p.id ?? idx}
                  className="relative bg-white rounded-2xl p-4 border border-slate-200 soft flex flex-col cursor-pointer hover:shadow-xl transition-transform duration-200 transform-gpu hover:scale-[1.04]"
                  onClick={() => {
                    setDetailItem(p);
                    setDetailOpen(true);
                  }}
                >
                  {isDistrib && hasActiveCouponFor(p.id) && (
                    <div className="absolute left-2 top-2 z-10">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                        Cupón disponible
                      </span>
                    </div>
                  )}

                  <div className="aspect-square overflow-hidden rounded-xl bg-white border">
                    <img
                      src={p.img || FALLBACK_IMG}
                      alt={p.nombre}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        if (e.currentTarget.src.endsWith("nodisponible.png")) return;
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = FALLBACK_IMG;
                      }}
                    />
                  </div>

                  <h4 className="mt-3 font-semibold text-slate-800 line-clamp-2 min-h-[2.75rem]" title={p.nombre}>
                    {truncateText(p.nombre, 68)}
                  </h4>

                  <div className="text-xs text-slate-500">{p.marca}</div>
                  <div className="text-xs text-slate-500">{p.presentacion}</div>
                  <div className={`mt-1 text-xs font-semibold ${hasStock ? "text-emerald-600" : "text-rose-600"}`}>
                    {hasStock ? `Stock disponible: ${available}` : "Sin stock disponible"}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">{money(priceWithRules(p.precioBase))}</div>

                  <button
                    className={`mt-3 btn btn-brand w-full ${hasStock ? "" : "opacity-50 cursor-not-allowed"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasStock) addToCart(p.id, 1);
                    }}
                    disabled={!hasStock}
                    aria-disabled={!hasStock}
                  >
                    Agregar
                  </button>

                  <button
                    className="mt-2 btn btn-light w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailItem(p);
                      setDetailOpen(true);
                    }}
                  >
                    Ver producto
                  </button>
                </article>
              );
            })}
          </div>
          {/* Paginación */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            <button className="px-3 py-2 rounded-lg border border-slate-200 disabled:opacity-40" onClick={() => setPage(1)} disabled={page === 1}>
              « Primero
            </button>
            <button className="px-3 py-2 rounded-lg border border-slate-200 disabled:opacity-40" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              ‹ Anterior
            </button>

            {(() => {
              const btns = [];
              const win = 2;
              const from = Math.max(1, page - win);
              const to = Math.min(totalPages, page + win);
              if (from > 1) {
                btns.push(1);
                if (from > 2) btns.push("…1");
              }
              for (let i = from; i <= to; i++) btns.push(i);
              if (to < totalPages) {
                if (to < totalPages - 1) btns.push("…2");
                btns.push(totalPages);
              }
              return btns.map((n, i) =>
                typeof n === "number" ? (
                  <button
                    key={i}
                    className={`min-w-[2.5rem] px-3 py-2 rounded-lg border ${n === page ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "border-slate-200"}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ) : (
                  <span key={i} className="px-2 text-slate-400 select-none">…</span>
                )
              );
            })()}

            <button className="px-3 py-2 rounded-lg border border-slate-200 disabled:opacity-40" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              Siguiente ›
            </button>
            <button className="px-3 py-2 rounded-lg border border-slate-200 disabled:opacity-40" onClick={() => setPage(totalPages)} disabled={page === totalPages}>
              Último »
            </button>

            <div className="w-full text-center text-sm text-slate-500 mt-2">
              Página {page} de {totalPages}
            </div>
          </div>
        </div>
      </main>

      {/* Drawer Carrito */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[75]">
          <div
            ref={backdropRef}
            role="presentation"
            className="absolute inset-0 bg-slate-900/60"
            onClick={(e) => {
              if (e.target === e.currentTarget) setDrawerOpen(false);
            }}
          />
          <aside
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl p-6 flex flex-col overflow-y-auto"
            onKeyDownCapture={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <h3 className="text-xl font-extrabold" style={{ color: "var(--ink)" }}>
                Tu Compra
              </h3>
              <button type="button" className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => setDrawerOpen(false)}>
                ✕
              </button>
            </div>

            {/* Auth en Drawer */}
            {!isAuthenticated && (
              <div className={`mt-4 rounded-xl p-4 shadow transition-colors duration-300 ease-in-out ${authOpen ? "bg-white border border-slate-200" : "bg-slate-800 border border-slate-700"}`}>
                <div className="flex items-center justify-between">
                  <h4 className={`font-semibold transition-colors duration-300 ${authOpen ? "text-slate-800" : "text-white"}`}>
                    ¿Tienes cuenta?
                  </h4>
                  <button
                    className={`font-semibold transition-colors duration-300 ${authOpen ? "text-sky-700" : "text-white"}`}
                    onClick={() => setAuthOpen((v) => !v)}
                  >
                    {authOpen ? "Ocultar" : "Iniciar sesión"}
                  </button>
                </div>

                {authOpen && (
                  <div className="mt-3 space-y-3">
                    {HAS_RECAPTCHA && (
                      <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={recaptchaRef} />
                    )}
                    <div className="flex gap-2 text-sm">
                      <button
                        className={`px-3 py-1 rounded-lg border transition-colors duration-200 ${authMode === "login" ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "border-slate-300"}`}
                        onClick={() => { setAuthMode("login"); setAuthErr(""); }}
                      >
                        Entrar
                      </button>
                      <button
                        className={`px-3 py-1 rounded-lg border transition-colors duration-200 ${authMode === "signup" ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "border-slate-300"}`}
                        onClick={() => { setAuthMode("signup"); setAuthErr(""); }}
                      >
                        Crear cuenta
                      </button>
                      <button
                        className={`px-3 py-1 rounded-lg border transition-colors duration-200 ${authMode === "forgot" ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "border-slate-300"}`}
                        onClick={() => { setAuthMode("forgot"); setAuthErr(""); }}
                      >
                        Recuperar
                      </button>
                    </div>

                    {authErr && (
                      <div className="p-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                        {authErr}
                      </div>
                    )}

                    {/* LOGIN */}
                    {authMode === "login" && (
                      <form className="space-y-2" onSubmit={doLogin}>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Correo</label>
                          <input
                            type="email"
                            value={loginData.user}
                            onChange={(e) => setLoginData((s) => ({ ...s, user: e.target.value }))}
                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Contraseña</label>
                          <PasswordInput
                            value={loginData.pass}
                            onChange={(e) => setLoginData((s) => ({ ...s, pass: e.target.value }))}
                            required
                            autoComplete="current-password"
                          />
                        </div>
                        <button className="btn btn-brand w-full" disabled={loadingAuth}>
                          {loadingAuth ? "Entrando…" : "Entrar"}
                        </button>
                      </form>
                    )}

                    {/* SIGNUP */}
                    {authMode === "signup" && (
                      <form className="space-y-2" onSubmit={doSignup}>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Nombre completo</label>
                          <input
                            value={signupData.nombre}
                            onChange={(e) => setSignupData((s) => ({ ...s, nombre: e.target.value }))}
                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                            required
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-sm font-medium text-slate-700">Empresa</label>
                            <input
                              value={signupData.empresa}
                              onChange={(e) => setSignupData((s) => ({ ...s, empresa: e.target.value }))}
                              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-700">WhatsApp (8 dígitos)</label>
                            <input
                              value={signupData.telefono}
                              onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, "").slice(-8);
                                setSignupData((s) => ({ ...s, telefono: v }));
                              }}
                              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                              placeholder="55667788"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Dirección</label>
                          <input
                            value={signupData.direccion}
                            onChange={(e) => setSignupData((s) => ({ ...s, direccion: e.target.value }))}
                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-sm font-medium text-slate-700">NIT</label>
                            <input
                              value={signupData.nit}
                              onChange={(e) => setSignupData((s) => ({ ...s, nit: e.target.value }))}
                              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                              placeholder="CF o tu NIT"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-700">Correo</label>
                            <input
                              type="email"
                              value={signupData.correo}
                              onChange={(e) => setSignupData((s) => ({ ...s, correo: e.target.value }))}
                              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-sm font-medium text-slate-700">Contraseña</label>
                            <PasswordInput
                              value={signupData.pass}
                              onChange={(e) => setSignupData((s) => ({ ...s, pass: e.target.value }))}
                              required
                              autoComplete="new-password"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-700">Confirmar contraseña</label>
                            <PasswordInput
                              value={signupData.passConfirm}
                              onChange={(e) => setSignupData((s) => ({ ...s, passConfirm: e.target.value }))}
                              required
                              autoComplete="new-password"
                            />
                          </div>
                        </div>

                        <button className="btn btn-brand w-full" disabled={loadingAuth}>
                          {loadingAuth ? "Enviando…" : "Crear cuenta"}
                        </button>
                      </form>
                    )}

                    {/* FORGOT / VERIFY / RESET */}
                    {authMode === "forgot" && (
                      <div className="space-y-2">
                        <p className="text-sm text-slate-600">
                          Ingresa tu correo y te enviaremos un código de verificación.
                        </p>
                        <input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2"
                          placeholder="tu@correo.com"
                        />
                        <div className="flex gap-2">
                          <button className="btn btn-light w-1/3" onClick={() => setAuthMode("login")}>
                            Volver
                          </button>
                          <button className="btn btn-brand w-2/3" onClick={onForgotSend} disabled={loadingAuth}>
                            {loadingAuth ? "Enviando…" : "Enviar código"}
                          </button>
                        </div>
                      </div>
                    )}

                    {authMode === "verify" && (
                      <div className="space-y-2">
                        <p className="text-sm text-slate-600">
                          Ingresa el código que enviamos a <b>{forgotEmail || "tu correo"}</b>.
                        </p>
                        <input
                          inputMode="numeric"
                          pattern="\d{6}"
                          maxLength={6}
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2"
                          placeholder="Código de 6 dígitos"
                        />
                        <div className="flex gap-2">
                          <button className="btn btn-light w-1/3" onClick={() => setAuthMode("forgot")}>
                            Volver
                          </button>
                          <button className="btn btn-brand w-2/3" onClick={onVerifyCode} disabled={loadingAuth}>
                            {loadingAuth ? "Validando…" : "Validar"}
                          </button>
                        </div>
                      </div>
                    )}

                    {authMode === "reset" && (
                      <div className="space-y-2">
                        <PasswordInput
                          value={resetData.pass}
                          onChange={(e) => setResetData((s) => ({ ...s, pass: e.target.value }))}
                          placeholder="Nueva contraseña"
                          required
                          autoComplete="new-password"
                        />
                        <PasswordInput
                          value={resetData.confirm}
                          onChange={(e) => setResetData((s) => ({ ...s, confirm: e.target.value }))}
                          placeholder="Confirmar contraseña"
                          required
                          autoComplete="new-password"
                        />
                        <div className="flex gap-2">
                          <button className="btn btn-light w-1/3" onClick={() => setAuthMode("login")}>
                            Cancelar
                          </button>
                          <button className="btn btn-brand" onClick={onResetPass} disabled={loadingAuth}>
                            {loadingAuth ? "Guardando…" : "Aceptar"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Lista del carrito */}
            <div className="mt-4 flex-1 overflow-auto divide-y divide-slate-200" id="cartList">
              {cartItems.length === 0 ? (
                <div className="py-8 text-center text-slate-500">Tu carrito está vacío.</div>
              ) : (
                cartItems.map((it) => {
                  const qty = Math.max(1, parseInt(it.qty || 1, 10));
                  const over = it.stock > 0 && qty > it.stock;
                  const maxReached = it.stock > 0 && qty >= it.stock; // para el botón +
                  return (
                    <div key={it.id} className="py-4 flex gap-3 items-center">
                      <img
                        src={it.img || FALLBACK_IMG}
                        alt={it.nombre}
                        className="h-16 w-16 rounded-lg border object-cover"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          if (e.currentTarget.src.endsWith("nodisponible.png")) return;
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = FALLBACK_IMG;
                        }}
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-slate-800">{it.nombre}</div>
                        <div className="text-sm text-slate-600">
                          {money(getEffectivePriceById(it.id))}
                        </div>
                        <div className="text-xs text-slate-500">
                          {it.stock > 0 ? `Stock: ${it.stock}` : "Sin stock"}
                        </div>

                        {/* Cantidad */}
                        <div className="mt-2 inline-flex items-center gap-2">
                          <button
                            className="rounded-lg border border-slate-200 px-3 py-2 text-lg disabled:opacity-50"
                            onClick={() => setQty(it.id, Math.max(1, qty - 1))}
                            aria-label="Disminuir cantidad"
                            disabled={qty <= 1}
                          >
                            -
                          </button>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={String(qty)}
                            onChange={(e) => {
                              const next = Math.max(1, parseInt((e.target.value || "1").replace(/\D/g, "") || "1", 10));
                              setQty(it.id, next);
                            }}
                            className="w-28 rounded-lg border border-slate-300 text-center text-lg font-semibold py-2"
                            title="Cantidad"
                          />
                          <button
                            className="rounded-lg border border-slate-200 px-3 py-2 text-lg disabled:opacity-50"
                            onClick={() => setQty(it.id, qty + 1)}
                            aria-label="Aumentar cantidad"
                            disabled={maxReached}
                          >
                            +
                          </button>
                        </div>

                        {/* Error de sobrestock */}
                        {over && (
                          <div className="mt-1 text-xs text-rose-600">
                            Stock solicitado supera el disponible
                          </div>
                        )}
                      </div>
                      <button
                        className="rounded-lg border border-slate-200 px-3 py-2 text-slate-600 hover:bg-slate-50"
                        title="Quitar"
                        onClick={() => removeItem(it.id)}
                      >
                        Quitar
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            {/* Resumen */}
            <div className="pt-4 border-t border-slate-200">
              {hasOverstock && (
                <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2">
                  Corrige las cantidades marcadas: hay productos que superan el stock disponible.
                </div>
              )}

              <div className="flex items-center justify-between text-slate-700">
                <span>Subtotal</span>
                <strong className="text-lg">{money(cartSubtotal)}</strong>
              </div>

              {isDistrib && couponDiscount > 0 && (
                <div className="mt-1 flex items-center justify-between text-emerald-700">
                  <span>
                    Descuento cupón
                    {selectedCoupon?.codigo ? ` (${selectedCoupon.codigo})` : ""}{" "}
                    {selectedCoupon?.producto_nombre ? `– ${selectedCoupon.producto_nombre}` : ""}
                  </span>
                  <strong>-{money(couponDiscount)}</strong>
                </div>
              )}

              {volumeDiscount > 0 && (
                <div className="mt-1 flex items-center justify-between text-amber-700">
                  <span>Descuento por volumen</span>
                  <strong>-{money(volumeDiscount)}</strong>
                </div>
              )}

              <div className="mt-1 flex items-center justify-between text-slate-700">
                <span>Subtotal con descuento</span>
                <strong>{money(Math.max(0, subtotalConDescuento - volumeDiscount))}</strong>
              </div>

              <div className="mt-1 flex items-center justify-between text-slate-700">
                <span>Envío {requiereDireccion ? "(según dirección)" : ""}</span>
                <strong>{money(envio)}</strong>
              </div>

              <div className="mt-2 border-t pt-2 flex items-center justify-between">
                <span>Total</span>
                <strong className="text-lg">{money(totalFinal)}</strong>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                  className="rounded-xl border border-slate-200 px-4 py-2 hover:bg-slate-50"
                  onClick={clearCart}
                >
                  Vaciar
                </button>
                <button
                  className="btn btn-brand text-center"
                  disabled={cartItems.length === 0 || hasOverstock}
                  onClick={openCheckout}
                >
                  Continuar
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Checkout */}
      {checkoutOpen && (
        <div className="fixed inset-0 z-[80]">
          <div
            role="presentation"
            className="absolute inset-0 bg-slate-900/60"
            onClick={(e) => {
              if (e.target === e.currentTarget) setCheckoutOpen(false);
            }}
          />
          <div
            className="relative mx-auto max-w-2xl w-[92%] bg-white rounded-2xl soft p-6 sm:p-8 mt-12 overflow-y-auto max-h-[92vh]"
            onKeyDownCapture={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-sm">
              <span className={step >= 0 ? "font-bold text-sky-700" : "text-slate-500"}>1. Identificación</span>
              <span>›</span>
              <span className={step >= 1 ? "font-bold text-sky-700" : "text-slate-500"}>2. Resumen</span>
              <span>›</span>
              <span className={step >= 2 ? "font-bold text-sky-700" : "text-slate-500"}>3. Pago</span>
              <span>›</span>
              <span className={step >= 3 ? "font-bold text-sky-700" : "text-slate-500"}>4. Verificación</span>
            </div>

            {step === 0 && (
              <div className="mt-6 space-y-4">
                <div className="flex flex-wrap gap-3">
                  {!isAuthenticated && (
                    <button className="btn btn-light" onClick={startLoginFromCheckout}>
                      Iniciar sesión (en el carrito)
                    </button>
                  )}
                  <button
                    className="btn btn-brand"
                    onClick={() => setUsuarioId(isAuthenticated ? user?.id : null)}
                  >
                    {isAuthenticated ? "Continuar con mi cuenta" : "Comprar sin iniciar sesión"}
                  </button>
                </div>
                {!usuarioId && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Nombre completo</label>
                      <input
                        value={guestNombre}
                        onChange={(e) => setGuestNombre(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">DPI (13 dígitos)</label>
                      <input
                        value={guestDpi}
                        maxLength={13}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, "");
                          if (v.length <= 13) setGuestDpi(v);
                        }}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-sm font-medium text-slate-700">WhatsApp (8 dígitos, sin 502)</label>
                      <input
                        value={guestWhatsapp}
                        maxLength={8}
                        inputMode="numeric"
                        pattern="\d{8}"
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 8);
                          setGuestWhatsapp(onlyDigits);
                        }}
                        placeholder="Ej. 59781234"
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        Ingresa solo el número local. Nosotros agregamos +502.
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-3 mt-4">
                  <button className="btn btn-brand" disabled={!validarPaso0()} onClick={() => setStep(1)}>
                    Siguiente
                  </button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="mt-6 space-y-4">
                <div className="grid gap-3">
                  {cartItems.map((it) => (
                    <div key={it.id} className="flex items-center gap-3 border rounded-xl p-3">
                      <img src={it.img || FALLBACK_IMG} alt={it.nombre} className="h-14 w-14 rounded-lg border object-cover"
                        onError={(e) => { if (!e.currentTarget.src.endsWith("nodisponible.png")) { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; }}} />
                      <div className="flex-1">
                        <div className="font-semibold">{it.nombre}</div>
                        <div className="text-sm text-slate-600">
                          x{Math.max(1, parseInt(it.qty || 1,10))} — {money(getEffectivePriceById(it.id) * Math.max(1, parseInt(it.qty || 1,10)))}
                        </div>
                        {it.stock > 0 && Number(it.qty) > Number(it.stock) && (
                          <div className="text-xs text-rose-600 mt-1">Stock solicitado supera el disponible</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {isDistrib && (
                    <div>
                      <label className="text-sm font-medium text-slate-700">Cupón</label>
                      <select
                        value={coupon}
                        onChange={(e) => {
                          const code = e.target.value;
                          setCoupon(code);
                          const found = cupones.find((x) => (x.codigo || x) === code);
                          setSelectedCoupon(found || null);
                        }}
                        disabled={cupones.length === 0}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
                      >
                        <option value="">
                          {cupones.length ? "Selecciona un cupón…" : "No hay cupones disponibles"}
                        </option>
                        {cupones.map((c, i) => (
                          <option key={`${c.id || c.codigo || i}`} value={c.codigo || c}>
                            {(c.codigo || c)} — {c.descripcion || "Cupón"}
                          </option>
                        ))}
                      </select>
                      {!!selectedCoupon?.producto_nombre && (
                        <p className="mt-1 text-xs text-slate-500">
                          Aplica a: <b>{selectedCoupon.producto_nombre}</b> ({selectedCoupon.porcentaje_descuento}%)
                        </p>
                      )}
                    </div>
                  )}

                  {requiereDireccion && (
                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        Dirección de entrega (requerida ≥ Q1,000) <br />
                        Comienza agregando Departamento y Municipio <br />
                        y luego coloca la dirección completa
                      </label>
                      <input
                        value={direccion}
                        onChange={(e) => setDireccion(e.target.value)}
                        placeholder="Ej.: Ciudad de Guatemala, zona X..."
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      />
                    </div>
                  )}
                </div>

                <div className="border rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span>Subtotal</span>
                    <strong>{money(cartSubtotal)}</strong>
                  </div>

                  {isDistrib && couponDiscount > 0 && (
                    <div className="flex items-center justify-between text-emerald-700">
                      <span>
                        Descuento cupón
                        {selectedCoupon?.codigo ? ` (${selectedCoupon.codigo})` : ""}{" "}
                      </span>
                      <strong>-{money(couponDiscount)}</strong>
                    </div>
                  )}

                  {volumeDiscount > 0 && (
                    <div className="flex items-center justify-between text-amber-700">
                      <span>Descuento por volumen</span>
                      <strong>-{money(volumeDiscount)}</strong>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span>Envío {requiereDireccion ? "(según dirección)" : ""}</span>
                    <strong>{money(envio)}</strong>
                  </div>
                  <div className="mt-2 border-t pt-2 flex items-center justify-between">
                    <span>Total</span>
                    <strong className="text-lg">{money(totalFinal)}</strong>
                  </div>

                  {!requiereNit && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2">
                      Compras menores a <b>Q100</b> no generan factura. No se solicitará NIT.
                    </div>
                  )}

                  <details className="mt-3">
                    <summary className="cursor-pointer text-sky-700 font-semibold">Ver políticas de envío</summary>
                    <div className="mt-2 text-sm text-slate-700 whitespace-pre-line">
{`🚚 Política de Envíos

📍 Ciudad de Guatemala
• Envío GRATIS en compras mayores a Q1,000
• Entrega directa a domicilio

🌍 Resto del país
• Envío por Cargo Expreso
• Flete incluido en el precio final
• Entregas rápidas y seguras a cualquier departamento.`}
                    </div>
                  </details>
                </div>

                <div className="flex justify-between">
                  <button className="btn btn-light" onClick={() => setStep(0)}>Atrás</button>
                  <button className="btn btn-brand" onClick={() => setStep(2)} disabled={(requiereDireccion && !direccion.trim()) || hasOverstock}>
                    Siguiente
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="mt-6 space-y-4">
                <div className="text-slate-700">
                  <h4 className="font-bold">🧾 Transferencia bancaria</h4>
                  <p>Realizá tu pago por transferencia y subí la foto del voucher directamente en la página.</p>
                  <ul className="list-disc ml-5 mt-2 text-sm">
                    <li>Validamos transferencias de lunes a viernes de 8 am a 6 pm.</li>
                    <li>Tiempo estimado de confirmación: 1–2 horas hábiles.</li>
                    <li>En caso de error o duplicación, contáctanos para reembolso/corrección.</li>
                  </ul>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">No. de boleta</label>
                    <input
                      value={boleta}
                      onChange={(e) => setBoleta(e.target.value)}
                      disabled={sending}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Banco</label>
                    <select
                      value={banco}
                      onChange={(e) => setBanco(e.target.value)}
                      disabled={sending}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
                    >
                      <option value="">Selecciona banco…</option>
                      <option value="Banco Industrial">Banco Industrial</option>
                    </select>

                    {banco === "Banco Industrial" && (
                      <div className="mt-2 rounded-lg border border-slate-200 p-3 bg-slate-50">
                        <div className="font-semibold text-slate-800">Banco Industrial</div>
                        <div className="text-sm text-slate-700">
                          Cuenta No. <b>2110001461</b>
                          <br />
                          Monetaria
                          <br />
                          a nombre de <b>PROMEQUI</b>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Subir voucher</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setVoucher(e.target.files?.[0] || null)}
                      disabled={sending}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
                    />
                  </div>
                  {requiereNit ? (
                    <div>
                      <label className="text-sm font-medium text-slate-700">NIT a facturar</label>
                      <input
                        value={nit}
                        onChange={(e) => setNit(e.target.value)}
                        placeholder="Ej. CF o tu NIT"
                        disabled={sending}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      />
                    </div>
                  ) : (
                    <div className="self-end w-full">
                      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2">
                        No se solicitará NIT porque el total a facturar es menor a <b>Q100</b>.
                      </div>
                    </div>
                  )}
                </div>

                {sending && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-slate-600">
                        {uploadPct === 100 ? "Completado" : "Subiendo voucher…"}
                      </span>
                      <span className="text-slate-500">
                        {typeof uploadPct === "number" ? `${uploadPct}%` : ""}
                      </span>
                    </div>
                    <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                      {typeof uploadPct === "number" ? (
                        <div
                          className={`h-full transition-all duration-200 ${uploadPct === 100 ? "bg-emerald-500" : "bg-sky-500"}`}
                          style={{ width: `${uploadPct}%` }}
                        />
                      ) : (
                        <div className="h-full w-1/2 bg-sky-500 animate-pulse" />
                      )}
                    </div>
                  </div>
                )}

                {sendError && <div className="text-red-600">{sendError}</div>}

                <div className="flex justify-between">
                  <button
                    className="btn btn-light"
                    onClick={() => { setStep(1); setUploadPct(null); }}
                    disabled={sending}
                  >
                    Atrás
                  </button>
                  <button
                    className="btn btn-brand"
                    onClick={() => setStep(3)}
                    disabled={sending || !boleta || !banco || !voucher || hasOverstock}
                  >
                    Continuar
                  </button>
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="mt-6 space-y-4">
                <div className="rounded-xl border border-slate-200 p-4 bg-white text-slate-800">
                  <h4 className="font-bold text-lg mb-2">Términos y Condiciones de Devolución de Productos.</h4>
                  <ol className="list-decimal ml-6 space-y-2 text-sm">
                    <li>
                      Aceptamos devoluciones dentro de los <b>30 días posteriores a la entrega</b> del producto.
                    </li>
                    <li>
                      Para procesar una devolución, el producto debe estar <b>sin uso, en perfectas condiciones y con su empaque original</b>.
                    </li>
                    <li>
                      Debes <b>contactar a nuestro equipo de atención al cliente</b> para coordinar el proceso. Dependiendo del caso, podrás elegir entre un <b>cambio por otro artículo, crédito en tienda, o reembolso</b> (según el método de pago original y las políticas de cada banco). Nos comprometemos a darte una respuesta clara y rápida.
                    </li>
                  </ol>
                  <label className="mt-4 flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-5 h-5"
                      checked={acceptedReturnsTC}
                      onChange={(e) => setAcceptedReturnsTC(e.target.checked)}
                    />
                    <span>Acepto los términos y condiciones de devolución de productos.</span>
                  </label>
                </div>

                <div className="flex justify-between">
                  <button className="btn btn-light" onClick={() => setStep(2)} disabled={sending}>
                    Atrás
                  </button>
                  <button
                    className="btn btn-brand"
                    onClick={enviarPedido}
                    disabled={sending || hasOverstock || !acceptedReturnsTC}
                  >
                    {sending ? `Enviando… ${typeof uploadPct === "number" ? `${uploadPct}%` : ""}` : "Enviar"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Detalle (más grande) */}
      {detailOpen && detailItem && (
        <div className="fixed inset-0 z-[85]">
          <div
            className="absolute inset-0 bg-slate-900/60"
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) setDetailOpen(false);
            }}
          />
          <div
            className="
              relative mx-auto w-[96%] max-w-6xl
              bg-white rounded-2xl soft
              p-6 sm:p-8 mt-6
              max-h-[94vh] overflow-y-auto
            "
            onKeyDownCapture={(e) => e.stopPropagation()}
          >
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="rounded-xl overflow-hidden border">
                <div className="aspect-square rounded-xl overflow-hidden border bg-white">
                  <img
                    src={detailItem.img || FALLBACK_IMG}
                    alt={detailItem.nombre}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { if (!e.currentTarget.src.endsWith("nodisponible.png")) { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; } }}
                  />
                </div>
              </div>
              <div className="min-w-0">
                <h3 className="text-3xl font-extrabold" style={{ color: "var(--ink)" }}>
                  {detailItem.nombre}
                </h3>
                {detailItem.marca && (
                  <div className="mt-2 text-slate-600">
                    <span className="font-semibold">Marca:</span> {detailItem.marca}
                  </div>
                )}
                {detailItem.presentacion && (
                  <div className="text-slate-600">
                    <span className="font-semibold">Presentación:</span> {detailItem.presentacion}
                  </div>
                )}

                {isDistrib && hasActiveCouponFor(detailItem.id) && (
                  <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                    Existe un <b>cupón válido</b> para este producto. Revísalo en el área de <b>Cupones</b> para aprovecharlo.
                  </div>
                )}

                <div className="mt-4 text-2xl font-bold">
                  {money(priceWithRules(detailItem.precioBase))}
                </div>

                <div className={`mt-2 text-sm font-semibold ${detailHasStock ? "text-emerald-600" : "text-rose-600"}`}>
                  {detailHasStock ? `Stock disponible: ${detailStock}` : "Sin stock disponible"}
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    className={`btn btn-brand flex-1 ${detailHasStock ? "" : "opacity-50 cursor-not-allowed"}`}
                    onClick={() => {
                      if (detailHasStock) {
                        addToCart(detailItem.id, 1);
                        setDetailOpen(false);
                      }
                    }}
                    disabled={!detailHasStock}
                    aria-disabled={!detailHasStock}
                  >
                    Agregar al carrito
                  </button>
                  <button className="btn btn-light flex-1" onClick={() => setDetailOpen(false)}>
                    Cancelar
                  </button>
                </div>
              </div>
            </div>

            <button
              className="absolute top-3 right-3 p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
              onClick={() => setDetailOpen(false)}
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-10 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center divide-y md:divide-y-0 md:divide-x divide-slate-300">
            <div className="w-full md:w-auto text-center px-0 md:px-8 pb-6 md:pb-0">
              <div className="font-extrabold tracking-wide text-slate-800">PROMEQUI</div>
              <div className="mt-2 text-m text-slate-600">
                2 Calle 16-53, Zona 4 Mixco <br />
                Col. Valle Del Sol <br />
                Teléfono:{" "}
                <a className="font-extrabold tracking-wide text-slate-800 underline" href="tel:+50224372562">2437-2562</a>{" "}
                /{" "}
                <a className="font-extrabold tracking-wide text-slate-800 underline" href="tel:+50224372503">2437-2503</a>{" "}
                /{" "}
                <a className="font-extrabold tracking-wide text-slate-800 underline" href="tel:+50224341904">2434-1904</a>{" "}
                <br />
                Correo: <a href="mailto:info@promequi.com.gt">info@promequi.com.gt</a> <br />
                Horario de atención: 09:00 am a 02:00 pm
              </div>
            </div>

            <div className="w-full md:w-auto text-center px-0 md:px-8 pt-6 md:pt-0">
              <a
                href="https://wa.me/50259786302?text=Hola%20Promequi%2C%20quisiera%20información"
                className="inline-flex"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Escríbenos por WhatsApp"
                title="WhatsApp"
              >
                <img src="/assets/whatsapp.png" alt="WhatsApp" className="h-16 w-16 mx-auto" />
                <p className="pl-8 flex items-center justify-center">¿CONSULTAS? 👈🏻</p>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Modales auxiliares */}
      <UserPanelModal open={panelOpen} onClose={() => setPanelOpen(false)} />

     {isAuthenticated && isDistrib && (
  <CuponesModal
    open={cuponesOpen}
    onClose={() => setCuponesOpen(false)}
    onSelect={(c) => {
      const code = typeof c === "string" ? c : c?.codigo || "";
      if (!code) return;

      const found = typeof c === "object" ? c : cupones.find((x) => (x.codigo || x) === code);

      setCoupon(code);
      setSelectedCoupon(found || null);

      setCupones((prev) => {
        const exists = prev.some((x) => (x.codigo || x) === code);
        const toAdd = found || { id: code, codigo: code, descripcion: "" };
        return exists ? prev : [...prev, toAdd];
      });

      setCuponesOpen(false);
    }}
    items={cupones}  
  />
)}

    </div>
  );
}
